import React, { useState, useEffect } from 'react';
import '../styles/TimerDialog.css';

interface TimerDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const TimerDialog: React.FC<TimerDialogProps> = ({ isOpen, onClose }) => {
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [timerType, setTimerType] = useState<'warmup' | 'break' | null>(null);
  const [isTimeUp, setIsTimeUp] = useState<boolean>(false);

  const startWarmupTimer = () => {
    setTimeLeft(240); // 4 minutes in seconds
    setTimerType('warmup');
    setIsRunning(true);
    setIsTimeUp(false);
  };

  const startBreakTimer = () => {
    setTimeLeft(90); // 90 seconds
    setTimerType('break');
    setIsRunning(true);
    setIsTimeUp(false);
  };

  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (isRunning && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft((prevTime) => {
          const newTime = prevTime - 1;
          
          if (newTime === 0) {
            setIsRunning(false);
            setIsTimeUp(true);
          }
          
          return newTime;
        });
      }, 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isRunning]);

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getClockHandColor = (): string => {
    if (isTimeUp) return '#ff0000';
    
    if (timerType === 'warmup' && timeLeft <= 120) {
      return '#ff0000'; // Red after 2 minutes (half time)
    }
    
    if (timerType === 'break' && timeLeft <= 15) {
      return '#ff0000'; // Red for last 15 seconds
    }
    
    return '#ffffff'; // Default white color
  };

  const getTimerClassName = (): string => {
    if (isTimeUp) return 'digital-timer time-up';
    
    if (
      (timerType === 'warmup' && timeLeft <= 120) || // Half time for warmup
      (timerType === 'break' && timeLeft <= 15)      // Last 15 seconds for break
    ) {
      return 'digital-timer warning';
    }
    
    return 'digital-timer';
  };

  const renderAnalogClock = () => {
    const totalSeconds = timerType === 'warmup' ? 240 : 90;
    const percentage = (timeLeft / totalSeconds) * 100;
    const degrees = (percentage / 100) * 360;

    return (
      <div className="analog-clock">
        <div 
          className="clock-hand"
          style={{
            transform: `rotate(${degrees}deg)`,
            backgroundColor: getClockHandColor()
          }}
        />
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="timer-dialog-overlay">
      <div className="timer-dialog">
        <div className="timer-header">
          <h2>Timer</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="timer-content">
          <div className="timer-displays">
            <div className={getTimerClassName()}>
              {isTimeUp ? 'TIME UP!' : formatTime(timeLeft)}
            </div>
            {renderAnalogClock()}
          </div>

          <div className="timer-buttons">
            <button 
              className="timer-button warmup"
              onClick={startWarmupTimer}
              disabled={isRunning}
            >
              Warm-up Timer (4 min)
            </button>
            <button 
              className="timer-button break"
              onClick={startBreakTimer}
              disabled={isRunning}
            >
              Break Timer (90 sec)
            </button>
            <button 
              className="timer-button return"
              onClick={onClose}
            >
              Return to Game
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimerDialog; 